// apps/web/src/router/routes.tsx — full data-router config.
//
// Permission gates are sourced 1:1 from `shell/menu.ts`. A route's permission
// is exactly the menu entry's `permission` for that path. If menu.ts ever
// diverges, one source-of-truth violation; update both.
import { createBrowserRouter, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { RootLayout } from '../shell/RootLayout.js';
import { AppShell } from '../shell/AppShell.js';
import { LandingRedirect } from '../shell/LandingRedirect.js';
import { NotFoundPage } from '../shell/NotFoundPage.js';
import { Placeholder } from '../shell/Placeholder.js';
import { AppErrorBoundary } from '../shell/AppErrorBoundary.js';
import { ModuleErrorBoundary } from '../shell/ModuleErrorBoundary.js';
import { ProtectedRoute } from './ProtectedRoute.js';
import { PermissionGate } from './RoleGate.js';
import { AdminGate } from './AdminGate.js';
import { LoginPage } from '../features/auth/index.js';
import { UsersListPage } from '../features/users/index.js';
import { ClientsListPage, ClientDetailPage } from '../features/clients/index.js';
import { DepartmentsListPage, DepartmentDetailPage } from '../features/departments/index.js';
import { RolesListPage } from '../features/roles/index.js';
import { AuditPage } from '../features/audit/index.js';
import { RequestsListPage } from '../features/requests/index.js';
import {
  DealsListPage,
  EngagementListPage,
  LeadCardPage,
  MyDayPage,
  SourcesPage,
  StagesPage,
} from '../features/crm/index.js';
import { CarriersPage, PriceListsPage, WarehousesPage } from '../features/carriers/index.js';
import { TransportTypesPage } from '../features/transport-types/index.js';
import { ArrivalCitiesPage } from '../features/arrival-cities/index.js';
import { RoadOptionsPage } from '../features/road-options/index.js';
import { RussiaCarriersPage } from '../features/russia-carriers/index.js';
import { PackagingOptionsPage } from '../features/packaging-options/index.js';
import { ProfilePage } from '../features/profile/index.js';
import { SettingsPage } from '../features/settings/index.js';
import { RevenueDashboardPage } from '../features/finance/revenue/index.js';
import { CrmIntakeAdminPage } from '../features/call-center-admin/index.js';
import {
  RussiaRequestDrawer,
  RussiaRequestEditor,
  RussiaRequestsListPage,
} from '../features/russia-requests/index.js';
import { WarehousesChinaPlaceholderPage } from '../features/warehouses-china/index.js';
import { CargoRegistryPage } from '../features/cargo/index.js';
import {
  CargoProblemCreateDrawer,
  CargoProblemDetailPage,
  CargoProblemsListPage,
} from '../features/cargo-problems/index.js';
import { ChinaRequestsDashboardPage } from '../features/dashboards/china-requests/index.js';
import { AgentPage, AgentSavedDashboardPage } from '../features/agent/index.js';
import {
  CommercialProposalDrawer,
  CommercialProposalEditor,
  CommercialProposalsListPage,
} from '../features/commercial-proposals/index.js';
import { PublicProposalPage } from '../features/commercial-proposals/public/index.js';
import {
  LogisticsCalculationCreatePage,
  LogisticsCalculationDetailPage,
  LogisticsCalculationsListPage,
} from '../features/logistics-calculations/index.js';
import { PublicLogisticsCalculationPage } from '../features/logistics-calculations/public/index.js';
import { CallCenterIntakeRoute } from '../features/call-center/index.js';
import { queryClient } from '../queryClient.js';
import { listRequests } from '../api/requests/index.js';
import { requestsKeys } from '../features/requests/queries.js';
import { detectStatusFilterRole, urlToWireFilters } from '../features/requests/filters.js';
import { useAuthStore } from '../stores/auth.js';

// WHY: the `/requests` loader kicks off a non-blocking prefetch keyed by
// the SAME URL-derived filter shape `RequestsListPage` queries on mount
// (issue #630). `urlToWireFilters` is the single source of truth for
// URL → wire derivation; loader and `useRequestsFilters` MUST share it
// so a shared link like `/requests?slaState=hot` produces a structurally
// equal TanStack query key here and in the component (warm-cache hit,
// no duplicate fetch). For URLs without any persisted filter key the
// helper falls back to `DEFAULT_REQUEST_LIST_STATUSES`, matching the All
// chip's default wire shape.
//
// `managerId` URLs skip prefetch deliberately: the page must validate
// hydrated manager IDs against `/requests/manager-options` before sending
// the first list request, so any prefetch here would either bypass that
// guard (sending unvalidated IDs) or warm a slot the post-validation
// query never reads. The slower first paint for shared manager-filter
// URLs is the accepted cost of correctness.
//
// localStorage is intentionally NOT read here. The loader runs on
// navigation; cross-tab/local-state persistence is the hook's domain.
//
// On cold boot — accessToken still null while `useAuthBootstrap` rotates
// the refresh cookie — short-circuit without firing the prefetch pre-auth;
// the in-component query runs after bootstrap completes and the cache
// populates then.
function requestsListLoader({ request }: LoaderFunctionArgs): null {
  const auth = useAuthStore.getState();
  if (!auth.accessToken) return null;
  const url = new URL(request.url);
  if (url.searchParams.has('managerId')) return null;
  // WHY (#1159 PR5): the loader's URL→wire derivation must use the same
  //   role-aware default fallback as `useRequestsFilters`, otherwise a
  //   logist's prefetch key (with the manager-default status list)
  //   would warm a slot the page never reads.
  const role = detectStatusFilterRole(auth.user);
  const initialFilters = urlToWireFilters(url, role);
  void queryClient.prefetchInfiniteQuery({
    queryKey: requestsKeys.listInfinite(initialFilters),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      listRequests({ ...initialFilters, cursor: pageParam as string | undefined }, { signal }),
  });
  return null;
}

// Exported so tests can build an in-memory router over the identical tree
// without duplicating the definition.
export const routeTree: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <AppErrorBoundary />,
    children: [
      { path: '/login', element: <LoginPage /> },
      {
        element: <ProtectedRoute />,
        errorElement: <ModuleErrorBoundary />,
        children: [
          // WHY (#1259): the external call-center intake page is a SIBLING of
          //   the AppShell route, not a child — so the internal sidebar/nav
          //   never mounts for a confined `call_center_agent`. The route's
          //   own element gates on `callCenterLeads.submit`; ProtectedRoute
          //   confines the agent here from every other path.
          { path: '/call-center/intake', element: <CallCenterIntakeRoute /> },
          {
            element: <AppShell />,
            children: [
              { path: '/', element: <LandingRedirect /> },

              // User-scoped routes (no PermissionGate — every authenticated
              // user must reach their own preferences and profile).
              { path: '/profile', element: <ProfilePage /> },
              { path: '/settings', element: <SettingsPage /> },

              // Work
              {
                element: <PermissionGate permissions={['tasks.read']} />,
                children: [{ path: '/my-day', element: <MyDayPage /> }],
              },
              // Commercial proposals (#1198). Mirrors /russia-rates: the
              //   outer gate is `commercialProposals.read`; the `new` create
              //   editor nests a `commercialProposals.manage` gate so a
              //   read-only actor cannot deep-link into the write surface.
              //   `:id` opens the detail drawer over the list `<Outlet />`.
              {
                element: <PermissionGate permissions={['commercialProposals.read']} />,
                children: [
                  {
                    path: '/kp',
                    element: <CommercialProposalsListPage />,
                    children: [
                      {
                        element: <PermissionGate permissions={['commercialProposals.manage']} />,
                        children: [{ path: 'new', element: <CommercialProposalEditor /> }],
                      },
                      { path: ':id', element: <CommercialProposalDrawer /> },
                    ],
                  },
                ],
              },
              {
                element: <PermissionGate permissions={['stages.read']} />,
                children: [{ path: '/queues', element: <Placeholder /> }],
              },
              {
                element: <PermissionGate permissions={['calls.read']} />,
                children: [{ path: '/call-center', element: <Placeholder /> }],
              },
              {
                element: <PermissionGate permissions={['audit.read']} />,
                children: [
                  { path: '/dashboard', element: <Placeholder /> },
                  { path: '/audit', element: <AuditPage /> },
                ],
              },

              // Logistics (#698)
              {
                element: <PermissionGate permissions={['requests.read']} />,
                children: [
                  {
                    path: '/china-rates',
                    element: <RequestsListPage />,
                    loader: requestsListLoader,
                  },
                ],
              },
              {
                element: <PermissionGate permissions={['russiaRequests.read']} />,
                children: [
                  {
                    path: '/russia-rates',
                    element: <RussiaRequestsListPage />,
                    children: [
                      // #706 Slice D: editor (manual create) mounts as a
                      //   sibling child route on `/new`; the drawer mounts on
                      //   `:id`. Both overlay the list page via `<Outlet />`.
                      //   `/new` is declared BEFORE `:id` so it doesn't get
                      //   swallowed as a stray id.
                      //
                      // WHY (Codex RUS-EDITOR-001): `/new` is a write surface
                      //   and the backend requires `russiaRequests.manage` to
                      //   POST. The outer gate is `russiaRequests.read` so a
                      //   read-only user could otherwise deep-link into the
                      //   editor, fill PII, and only learn on submit that the
                      //   action is forbidden. Nest a manage-permission gate
                      //   on the `/new` path so the editor never mounts for a
                      //   read-only actor.
                      {
                        element: <PermissionGate permissions={['russiaRequests.manage']} />,
                        children: [
                          { path: 'new', element: <RussiaRequestEditor /> },
                          // WHY (#1571): editing an existing DRAFT/RETURN draft
                          //   is a write surface too — the backend requires
                          //   `russiaRequests.manage` to PATCH the row, items,
                          //   and totals. Nest it under the same manage gate as
                          //   `new`, and declare `:id/edit` BEFORE the `:id`
                          //   drawer so the deeper path wins.
                          { path: ':id/edit', element: <RussiaRequestEditor /> },
                        ],
                      },
                      { path: ':id', element: <RussiaRequestDrawer /> },
                    ],
                  },
                ],
              },
              {
                element: <PermissionGate permissions={['warehousesChina.read']} />,
                children: [
                  { path: '/warehouses-china', element: <WarehousesChinaPlaceholderPage /> },
                ],
              },
              {
                element: <PermissionGate permissions={['cargo.read']} />,
                children: [{ path: '/cargo', element: <CargoRegistryPage /> }],
              },
              {
                element: <PermissionGate permissions={['cargoProblems.read']} />,
                children: [
                  {
                    path: '/cargo/problems',
                    element: <CargoProblemsListPage />,
                    children: [
                      {
                        element: <PermissionGate permissions={['cargoProblems.create']} />,
                        children: [{ path: 'new', element: <CargoProblemCreateDrawer /> }],
                      },
                    ],
                  },
                  { path: '/cargo/problems/:id', element: <CargoProblemDetailPage /> },
                ],
              },
              {
                element: <PermissionGate permissions={['logisticsCalculations.read']} />,
                children: [
                  { path: '/ttn', element: <LogisticsCalculationsListPage /> },
                  {
                    element: <PermissionGate permissions={['logisticsCalculations.manage']} />,
                    children: [{ path: '/ttn/new', element: <LogisticsCalculationCreatePage /> }],
                  },
                  { path: '/ttn/:id', element: <LogisticsCalculationDetailPage /> },
                ],
              },

              // CRM
              {
                element: <PermissionGate permissions={['leads.read']} />,
                children: [
                  { path: '/leads', element: <EngagementListPage /> },
                  { path: '/leads/:id', element: <LeadCardPage /> },
                ],
              },
              {
                element: <PermissionGate permissions={['deals.read']} />,
                children: [{ path: '/deals', element: <DealsListPage /> }],
              },
              {
                element: <PermissionGate permissions={['clients.read']} />,
                children: [
                  { path: '/clients', element: <ClientsListPage /> },
                  { path: '/clients/:id', element: <ClientDetailPage /> },
                ],
              },

              // Dashboards (#1587). Phase-1 admin-only preview: no role holds
              //   `dashboard.read` yet (catalog-grow only), so only admin
              //   reaches this via the `ALL` sentinel. The placeholder page
              //   renders no metrics — just the section shell.
              {
                element: <PermissionGate permissions={['dashboard.read']} />,
                children: [
                  {
                    path: '/dashboards/china-requests',
                    element: <ChinaRequestsDashboardPage />,
                  },
                  // Agent page (#1876). Leadership-gated AI analyst chat
                  //   workspace; same `dashboard.read` gate as the sibling.
                  { path: '/dashboards/agent', element: <AgentPage /> },
                  { path: '/dashboards/my', element: <AgentSavedDashboardPage /> },
                ],
              },

              // Finance
              {
                element: <AdminGate />,
                children: [{ path: '/finance/revenue', element: <RevenueDashboardPage /> }],
              },
              {
                element: <PermissionGate permissions={['paymentClaims.read']} />,
                children: [{ path: '/finance/payment-claims', element: <Placeholder /> }],
              },

              // Reference data
              {
                element: <PermissionGate permissions={['carriers.read']} />,
                children: [{ path: '/carriers', element: <CarriersPage /> }],
              },
              {
                element: <PermissionGate permissions={['warehouses.read']} />,
                children: [{ path: '/warehouses', element: <WarehousesPage /> }],
              },
              {
                element: <PermissionGate permissions={['priceLists.read']} />,
                children: [{ path: '/price-lists', element: <PriceListsPage /> }],
              },
              {
                element: (
                  <PermissionGate
                    permissions={['transportTypes.read']}
                    denySystemRoles={['manager']}
                  />
                ),
                children: [{ path: '/transport-types', element: <TransportTypesPage /> }],
              },
              {
                element: (
                  <PermissionGate
                    permissions={['arrivalCities.read']}
                    denySystemRoles={['manager']}
                  />
                ),
                children: [{ path: '/arrival-cities', element: <ArrivalCitiesPage /> }],
              },
              {
                element: <PermissionGate permissions={['roads.manage']} />,
                children: [{ path: '/road-options', element: <RoadOptionsPage /> }],
              },
              {
                element: <PermissionGate permissions={['packaging.manage']} />,
                children: [{ path: '/packaging-options', element: <PackagingOptionsPage /> }],
              },
              {
                element: <PermissionGate permissions={['russiaCarriers.read']} />,
                children: [{ path: '/russia-carriers', element: <RussiaCarriersPage /> }],
              },
              {
                element: (
                  <PermissionGate permissions={['sources.read']} denySystemRoles={['manager']} />
                ),
                children: [{ path: '/sources', element: <SourcesPage /> }],
              },
              {
                element: <PermissionGate permissions={['stages.read']} />,
                children: [{ path: '/stages', element: <StagesPage /> }],
              },

              // Administration
              {
                element: <PermissionGate permissions={['users.manage']} />,
                children: [
                  { path: '/users', element: <UsersListPage /> },
                  { path: '/users/:id', element: <UsersListPage /> },
                ],
              },
              {
                element: <PermissionGate permissions={['departments.manage']} />,
                children: [
                  { path: '/departments', element: <DepartmentsListPage /> },
                  { path: '/departments/:id', element: <DepartmentDetailPage /> },
                ],
              },
              {
                element: <PermissionGate permissions={['roles.read']} />,
                children: [{ path: '/roles', element: <RolesListPage /> }],
              },
              {
                element: <AdminGate />,
                children: [{ path: '/crm-intake-admin', element: <CrmIntakeAdminPage /> }],
              },

              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
  // WHY (#1199): the public proposal page is intentionally a TOP-LEVEL
  //   sibling of the RootLayout tree — NOT nested under RootLayout /
  //   ProtectedRoute / AppShell — so it never mounts `useAuthBootstrap`. A
  //   logged-out customer opening `/p#t=<token>` must reach the page with no
  //   token, no /auth/refresh, and no auth state on this anonymous surface.
  { path: '/p', element: <PublicProposalPage />, errorElement: <AppErrorBoundary /> },
  { path: '/r', element: <PublicLogisticsCalculationPage />, errorElement: <AppErrorBoundary /> },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routeTree);
